import { useCallback, useEffect, useRef, useState } from 'react';
import { createLocalConversationId, saveConversationSnapshot, removeConversationSnapshot } from './conversationRepository';

const signature = (messages) => JSON.stringify(messages);
const ownsAccount = (userId) => userId != null && localStorage.getItem('userId') === String(userId);

export default function useOfflineConversation({ enabled, userId, conversationId, messages, agentId, projectId, busy }) {
  const active = useRef(null);
  const queue = useRef(Promise.resolve());
  const mounted = useRef(true);
  const [status, setStatus] = useState({ session: null, value: '' });
  const account = userId == null ? null : String(userId);
  const scope = JSON.stringify([account, agentId, projectId || null]);
  const previous = active.current;
  const sameIdentity = previous && previous.scope === scope && (
    previous.inputId === conversationId || (conversationId && previous.redirect === conversationId)
  ) && !(messages.length === 0 && previous.latest.messages.length > 0 && !conversationId);
  if (!sameIdentity) {
    active.current = { scope, inputId: conversationId, id: conversationId || createLocalConversationId(),
      revision: 0, redirect: null, lastSaved: null, finalSignature: null, checkpointSignature: null };
  }
  const session = active.current;
  session.inputId = conversationId;
  session.latest = { userId: account, messages, agentId, projectId };

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const report = useCallback((target, value) => {
    if (mounted.current && active.current === target) setStatus({ session: target, value });
  }, []);
  const serialize = useCallback((operation) => {
    const result = queue.current.then(operation);
    queue.current = result.catch(() => {});
    return result;
  }, []);

  const write = useCallback((target, state, interrupted = false) => {
    if (!enabled || !state.messages.length || !ownsAccount(state.userId)) return Promise.resolve(null);
    const revision = ++target.revision;
    const contentSignature = signature(state.messages);
    return serialize(async () => {
      if (!ownsAccount(state.userId)) return null;
      try {
        const id = target.redirect || target.id;
        const saved = await saveConversationSnapshot(state.userId, {
          id, userId: state.userId,
          title: (state.messages.find((message) => message.isUser)?.content || '').slice(0, 60),
          messages: state.messages.map((message) => ({ ...message, agent_id: state.agentId })),
          agent_id: state.agentId, projectId: state.projectId || null,
          updatedAt: new Date().toISOString(), localOnly: String(id).startsWith('local:'),
          pendingLocal: true, syncStatus: 'pending', interrupted, offlineRevision: revision,
        }, { source: 'local' });
        target.lastSaved = saved;
        target.checkpointSignature = contentSignature;
        if (!interrupted) target.finalSignature = contentSignature;
        report(target, interrupted ? 'interrupted' : 'saved');
        return saved;
      } catch (_) {
        target.checkpointSignature = null; // allow a later checkpoint to retry local storage
        report(target, 'error');
        return null;
      }
    });
  }, [enabled, report, serialize]);

  useEffect(() => {
    if (!enabled || !busy) return undefined;
    const checkpoint = () => {
      // Capture this conversation's state, including during effect cleanup on selection changes.
      const state = session.latest;
      const contentSignature = signature(state.messages);
      if (contentSignature === session.checkpointSignature || contentSignature === session.finalSignature) return;
      session.checkpointSignature = contentSignature;
      void write(session, state, true);
    };
    checkpoint();
    const timer = setInterval(checkpoint, 1000);
    return () => { clearInterval(timer); checkpoint(); };
  }, [enabled, busy, session, write]);

  // A callback retained by an older request retains its original conversation identity.
  const checkpoint = useCallback((content) => write(session, { ...session.latest, messages: content }, true), [session, write]);
  const persist = useCallback((content) => write(session, { ...session.latest, messages: content }, false), [session, write]);
  const confirm = useCallback((snapshot, serverId) => {
    if (!enabled || !snapshot || !serverId || !ownsAccount(snapshot.userId)) return Promise.resolve();
    return serialize(async () => {
      if (!ownsAccount(snapshot.userId)) return;
      try {
        const newer = session.lastSaved && session.lastSaved.offlineRevision > snapshot.offlineRevision;
        const content = newer ? session.lastSaved : snapshot;
        await saveConversationSnapshot(snapshot.userId, {
          ...content, id: serverId, localOnly: false, pendingLocal: Boolean(newer),
          syncStatus: newer ? 'pending' : 'synced', interrupted: newer ? content.interrupted : false,
        }, { source: newer ? 'local' : 'server', force: true });
        // Redirect before deleting: queued checkpoints now write only to the acknowledged ID.
        session.redirect = serverId;
        if (snapshot.id !== serverId) await removeConversationSnapshot(snapshot.userId, snapshot.id);
        report(session, newer && content.interrupted ? 'interrupted' : 'saved');
      } catch (_) { report(session, 'error'); }
    });
  }, [enabled, session, serialize, report]);
  return { checkpoint, persist, confirm, storageState: status.session === session ? status.value : '' };
}
